import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import type { Interpolation } from 'styled-components';
import styled from 'styled-components';

import SecondaryText from 'components/core/typography/SecondaryText';
import InputText from 'components/ui/forms/shared/InputText';
import { Input } from 'components/ui/forms/shared/InputText.styles';
import { CloseIcon, SearchIcon } from 'components/ui/icons';
import { ElementTestId } from 'enums/testing';
import useDebounceKeywordSearch from 'hooks/useKeywordSearch';
import { translate } from 'utils/intlUtils';
import { DEFAULT_SEARCH_DEBOUNCE } from 'utils/timeUtils';

import { BODY_TEXT, BORDER_DEFAULT } from 'styles/color';
import {
  BORDER_RADIUS_200,
  HEIGHT_400,
  LINE_HEIGHT_2,
  NEUTRAL_0,
  SPACE_200,
  SPACE_300,
  WIDTH_400,
  Z_INDEX_1,
} from 'styles/tokens';

const SearchContainer = styled(SecondaryText)<{ $searchContainerCss?: Interpolation<object>; $width?: number }>`
  align-items: center;
  background: ${NEUTRAL_0};
  border-radius: ${BORDER_RADIUS_200};
  box-shadow: 0 0 0 1px ${BORDER_DEFAULT} inset;
  color: ${BODY_TEXT};
  column-gap: ${SPACE_200};
  display: flex;
  height: 100%;
  padding: ${SPACE_200} ${SPACE_300};
  width: ${({ $width }) => ($width ? `${$width}px` : '100%')};

  ${props => props.$searchContainerCss}
`;

const SearchInputText = styled(InputText)`
  ${Input} {
    line-height: ${LINE_HEIGHT_2};
    padding: 0;
  }
`;

const ExtendedSearchIcon = styled(SearchIcon)`
  height: ${HEIGHT_400};
  pointer-events: none;
  width: ${WIDTH_400};
  z-index: ${Z_INDEX_1};
`;

export interface SearchInputRef {
  clear: () => void;
  focus: () => void;
  input: () => HTMLInputElement;
}

interface SearchProps {
  /**
   * Automatically focus the input on mount
   */
  autoFocus?: boolean;
  /**
   * Optional class name to be applied to the input component
   */
  className?: string;
  /**
   * Time in milliseconds to debounce the input value before triggering onChange
   */
  debounceTime?: null | number;
  /**
   * Default value for the search input
   */
  defaultValue?: string;
  /**
   * Callback function triggered when the input value changes (after debounce)
   */
  onChange?: (event) => void;
  /**
   * Callback function triggered when the search is cleared (Escape key or close icon)
   */
  onClose?: () => void;
  /**
   * Placeholder text for the search input
   */
  placeholder?: string;
  /**
   * Ref forwarding to expose input methods: clear, focus, and get input element
   */
  ref?: any;
  /**
   * Additional custom styles to override the search container styles
   */
  searchContainerCss?: Interpolation<object>;
  /**
   * Optional test ID for querying the input in tests
   */
  testId?: string;
  /**
   * Custom width (in pixels) for the search input container
   */
  width?: number;
}

const SearchInput = forwardRef<SearchInputRef | undefined, SearchProps>(
  (
    {
      autoFocus,
      className,
      debounceTime = DEFAULT_SEARCH_DEBOUNCE,
      defaultValue,
      onChange = () => {},
      onClose,
      placeholder,
      searchContainerCss,
      testId,
      width,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(defaultValue || '');
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [prevDefaultValue, setPrevDefaultValue] = useState(defaultValue);

    /*
     * Clear the local input value when the parent externally resets `defaultValue` to falsy,
     * while still letting typing and the close icon update it optimistically. Tracking the prop
     * in lockstep (rather than via `usePrevious`) ensures this only fires once per actual change.
     */
    if (prevDefaultValue !== defaultValue) {
      setPrevDefaultValue(defaultValue);
      if (prevDefaultValue && !defaultValue) {
        setInputValue('');
      }
    }

    const onCloseCallback = useCallback(() => setInputValue(''), [setInputValue]);
    const onChangeCallback = useCallback(e => setInputValue(e?.currentTarget?.value || ''), [setInputValue]);
    const onKeyUpCallback = useCallback(
      e => {
        if (e?.keyCode !== 27) {
          return;
        }

        onClose?.();
        setInputValue('');
        e.stopPropagation();
      },
      [onClose]
    );

    useDebounceKeywordSearch(inputValue, onChange, debounceTime || 0);

    useImperativeHandle(ref, (): SearchInputRef => ({
      clear: () => {
        setInputValue('');
      },
      focus: () => {
        inputRef?.current?.focus();
      },
      input: () => inputRef?.current as HTMLInputElement,
    }));

    return (
      <SearchContainer $searchContainerCss={searchContainerCss} $width={width}>
        <ExtendedSearchIcon />
        <SearchInputText
          autoFocus={autoFocus}
          data-testid={testId || ElementTestId.SEARCH_INPUT}
          onChange={onChangeCallback}
          onKeyUp={onKeyUpCallback}
          placeholder={placeholder || translate.t('search')}
          ref={inputRef}
          value={inputValue}
          variant="none"
          {...props}
        />
        {!!inputValue && <CloseIcon data-testid={ElementTestId.CLOSE_ICON} onClick={onCloseCallback} />}
      </SearchContainer>
    );
  }
);

export default SearchInput;
